// Stage 1: 招聘规划&需求 - 5个模块
import { useApi, api } from '../api/client';
import { useActions, exportCsv } from '../actions';
import {
  PageHeader, ModuleBanner, Card, CardHead, Kpi, Tag, Status, Loading, ErrorBox, BudgetBar,
} from '../components/UI';
import { MODULE_META } from '../config';
import { useAI } from '../components/AIPanel';

// ===== 1.1 人力需求提报 =====
export function NeedReport() {
  const meta = MODULE_META['need-report'];
  const { openCreate } = useActions();
  const { data, loading, error } = useApi(() => api.needs(), [], 'needs');
  if (loading) return <Loading />;
  if (error || !data) return <ErrorBox message={error || ''} />;

  return (
    <div className="fade-in">
      <PageHeader
        title={meta.title}
        subtitle={meta.subtitle}
        stageBadge={meta.stageBadge}
        actions={
          <>
            <button className="btn btn-primary" onClick={() => openCreate('needs')}>+ 新建需求</button>
            <button className="btn btn-ghost" onClick={() => exportCsv(data as any, '招聘需求.csv')}>导出</button>
          </>
        }
      />
      <ModuleBanner title="需求池 · 全部在招 HC" stageTag="Stage 1-1" />
      <Card>
        <CardHead title={<>需求列表 <Tag color="cinnabar">{data.length} 项</Tag></>} />
        <div className="table-scroll">
        <table className="table">
          <thead>
            <tr>
              <th>需求编号</th><th>二级部门</th><th>三级部门</th><th>招聘岗位</th>
              <th>人数</th><th>招聘城市</th><th>优先级</th><th>状态</th>
              <th>责任人</th><th>业务一面</th><th>HR二面</th><th>终面</th>
              <th>招聘周期</th><th>编制内</th>
            </tr>
          </thead>
          <tbody>
            {data.map(n => (
              <tr key={n.id}>
                <td className="mono text-xs">{n.id}</td>
                <td>{n.dept}</td>
                <td className="text-sm text-ink-3">{n.subDept || '-'}</td>
                <td><b>{n.position}</b></td>
                <td>{n.count}</td>
                <td className="text-sm">{n.city || '-'}</td>
                <td>
                  <Tag color={n.priority === 'P0' ? 'cinnabar' : n.priority === 'P1' ? 'gold' : 'outline'}>
                    {n.priority}
                  </Tag>
                </td>
                <td>
                  <Status type={n.status === '招聘中' ? 'progress' : n.status === '已入职' ? 'done' : 'pending'}>
                    {n.status}
                  </Status>
                </td>
                <td className="text-sm">{n.recruiter}</td>
                <td className="text-sm">{n.interview1 || '-'}</td>
                <td className="text-sm">{n.interview2 || '-'}</td>
                <td className="text-sm">{n.interview3 || '-'}</td>
                <td className="text-xs text-ink-3">
                  {n.startDate && n.endDate ? `${n.startDate} ~ ${n.endDate}` : n.startDate || '-'}
                </td>
                <td className="text-sm">{n.inBudget || '-'}</td>
              </tr>
            ))}
          </tbody>
        </table>
        </div>
      </Card>
    </div>
  );
}

// ===== 1.2 需求评审 =====
export function NeedReview() {
  const meta = MODULE_META['need-review'];
  const { openCreate } = useActions();
  const { data, loading, error } = useApi(() => api.reviews(), [], 'reviews');
  if (loading) return <Loading />;
  if (error || !data) return <ErrorBox message={error || ''} />;

  return (
    <div className="fade-in">
      <PageHeader
        title={meta.title}
        subtitle={meta.subtitle}
        stageBadge={meta.stageBadge}
        actions={<button className="btn btn-primary" onClick={() => openCreate('reviews')}>+ 发起评审</button>}
      />
      <ModuleBanner title="评审/审批记录" stageTag="Stage 1-2" />

      <div className="grid grid-3" style={{ marginBottom: 24 }}>
        <Kpi label="本月评审" value={15} unit="次" trend={{ type: 'up', text: '通过 12 · 待审 3' }} />
        <Kpi label="新增 HC" value={28} unit="人" trend={{ type: 'up', text: '↑ 8 较上月' }} />
        <Kpi label="预算总额" value="1,860" unit="万/年" />
      </div>

      <Card>
        <CardHead title="审批流程" />
        <table className="table">
          <thead>
            <tr>
              <th>编号</th><th>岗位</th><th>部门</th><th>提报日期</th>
              <th>审核人</th><th>结果</th><th>HC</th><th>预算</th><th>备注</th>
            </tr>
          </thead>
          <tbody>
            {data.map(r => (
              <tr key={r.id}>
                <td className="mono text-xs">{r.id}</td>
                <td><b>{r.position}</b></td>
                <td>{r.dept}</td>
                <td className="text-sm">{r.submitted}</td>
                <td className="text-sm">{r.reviewer}</td>
                <td><Status type={r.result === '通过' ? 'done' : 'progress'}>{r.result}</Status></td>
                <td>{r.headcount}</td>
                <td className="mono text-xs">{r.budget}</td>
                <td className="text-sm text-ink-3">{r.remark}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </Card>
    </div>
  );
}

// ===== 1.3 JD 库 =====
export function JdLibrary() {
  const meta = MODULE_META['jd-library'];
  const { toast, openCreate } = useActions();
  const { open: openAI } = useAI();
  const { data, loading, error } = useApi(() => api.jds(), [], 'jds');
  if (loading) return <Loading />;
  if (error || !data) return <ErrorBox message={error || ''} />;

  return (
    <div className="fade-in">
      <PageHeader
        title={meta.title}
        subtitle={meta.subtitle}
        stageBadge={meta.stageBadge}
        actions={
          <>
            <button className="btn btn-primary" onClick={() => openCreate('jds')}>+ 新建 JD</button>
            <button className="btn btn-ghost" onClick={() => toast('模板库开发中')}>导入模板</button>
            <button className="btn btn-accent" onClick={() => openAI('AI 优化 JD', (m) => api.aiChat([{role:'user',content:'请帮我优化一个高级前端工程师的JD，要求包含岗位职责和任职要求，用markdown格式输出'}]))}>AI 优化</button>
          </>
        }
      />
      <ModuleBanner title="JD 库 · 标准化岗位说明书" stageTag="Stage 1-3" />

      <div className="grid grid-3">
        {data.map(j => (
          <div key={j.code} className="jd-card">
            <div className="jd-n">{j.code} · {j.level}</div>
            <div className="jd-title">{j.title}</div>
            <div className="jd-info">
              <div className="jd-info-item">
                <div className="lbl">部门</div>
                <div className="val text-sm">{j.dept}</div>
              </div>
              <div className="jd-info-item">
                <div className="lbl">薪资</div>
                <div className="val text-sm">{j.salary}</div>
              </div>
              <div className="jd-info-item">
                <div className="lbl">地点</div>
                <div className="val text-sm">{j.location}</div>
              </div>
              <div className="jd-info-item">
                <div className="lbl">汇报</div>
                <div className="val text-sm">{j.report_to}</div>
              </div>
            </div>
            <div className="jd-section">
              <h5>岗位职责</h5>
              <ul>{j.duties.map((d, i) => <li key={i}>{d}</li>)}</ul>
            </div>
            <div className="jd-section">
              <h5>任职要求</h5>
              <ul>{j.requires.map((r, i) => <li key={i}>{r}</li>)}</ul>
            </div>
            <div className="flex gap-2" style={{ marginTop: 12 }}>
              <button className="btn btn-ghost" style={{ flex: 1 }} onClick={() => toast('编辑功能开发中')}>编辑</button>
              <button className="btn btn-primary" style={{ flex: 1 }} onClick={() => toast('已发布到渠道')}>发布到渠道</button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

// ===== 1.4 渠道规划 =====
export function ChannelPlan() {
  const meta = MODULE_META['channel-plan'];
  const { openCreate } = useActions();
  const { data, loading, error } = useApi(() => api.channels(), [], 'channels');
  if (loading) return <Loading />;
  if (error || !data) return <ErrorBox message={error || ''} />;

  return (
    <div className="fade-in">
      <PageHeader
        title={meta.title}
        subtitle={meta.subtitle}
        stageBadge={meta.stageBadge}
        actions={<button className="btn btn-primary" onClick={() => openCreate('channels')}>+ 新增渠道</button>}
      />
      <ModuleBanner title="渠道矩阵 · 9 大渠道运营策略" stageTag="Stage 1-4" />

      <div className="grid grid-3" style={{ marginBottom: 24 }}>
        <Kpi label="在用渠道" value={9} unit="个" />
        <Kpi label="简历贡献" value={540} unit="份" trend={{ type: 'up', text: '↑ 23% vs 上月' }} />
        <Kpi label="最佳 ROI" value={9.0} />
      </div>

      <div className="channel-grid">
        {data.map(c => (
          <div key={c.name} className="channel-card">
            <div className="ch-head">
              <div className="ch-logo">{c.short}</div>
              <div>
                <div className="ch-name">{c.name}</div>
                <div className="ch-type">{c.type} · <Tag color={c.status === '主投' ? 'cinnabar' : c.status === '辅助' ? 'gold' : 'outline'}>{c.status}</Tag></div>
              </div>
            </div>
            <div className="ch-stats">
              <div className="ch-stat">
                <div className="ch-stat-n">{c.resume}</div>
                <div className="ch-stat-l">简历</div>
              </div>
              <div className="ch-stat">
                <div className="ch-stat-n">{c.interview}</div>
                <div className="ch-stat-l">面试</div>
              </div>
              <div className="ch-stat">
                <div className="ch-stat-n text-cinnabar">{c.hire}</div>
                <div className="ch-stat-l">入职</div>
              </div>
            </div>
            <div style={{ marginTop: 12 }} className="flex justify-between text-xs">
              <span className="text-ink-3">成本 ¥{c.cost.toLocaleString()}</span>
              <span className="text-bamboo fw-600">ROI {c.roi}</span>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

// ===== 1.5 预算 =====
export function Budget() {
  const meta = MODULE_META['budget'];
  const { openCreate } = useActions();
  const { data, loading, error } = useApi(() => api.budget(), [], 'budget');
  if (loading) return <Loading />;
  if (error || !data) return <ErrorBox message={error || ''} />;

  return (
    <div className="fade-in">
      <PageHeader
        title={meta.title}
        subtitle={meta.subtitle}
        stageBadge={meta.stageBadge}
        actions={
          <>
            <button className="btn btn-primary" onClick={() => openCreate('budget')}>+ 调整预算</button>
            <button className="btn btn-ghost" onClick={() => exportCsv(data as any, '招聘预算.csv')}>下载报表</button>
          </>
        }
      />
      <ModuleBanner title="Q3 招聘预算执行" stageTag="Stage 1-5" />

      <div className="grid grid-4" style={{ marginBottom: 24 }}>
        <Kpi label="总预算" value="26.7" unit="万" />
        <Kpi label="已使用" value="10.5" unit="万" />
        <Kpi label="单入职成本" value="¥2,840" />
        <Kpi label="预算余额" value="16.2" unit="万" />
      </div>

      <Card>
        <CardHead title="预算执行明细" />
        {data.map(b => (
          <BudgetBar key={b.id} name={b.name} planned={b.planned} used={b.used} color={b.color} />
        ))}
      </Card>
    </div>
  );
}
